import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Badge, Button, Col, Form, InputGroup, Row, Spinner } from 'react-bootstrap';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import Paginacao from '../../components/Paginacao';
import DetalheImovel from './DetalheImovel';
import TabelaImoveis from './TabelaImoveis';
import BuscaAvancadaImoveis, { descreverFiltros } from './BuscaAvancadaImoveis';

// Mesmas opções de busca do painel legado (Referência, Referência auxiliar, Tipo,
// Bairro, Condomínio) + "Todos" (procura em vários campos de uma vez) e Cidade.
const CAMPOS_BUSCA = [
  { valor: '', rotulo: 'Todos os campos' },
  { valor: 'referencia', rotulo: 'Referência' },
  { valor: 'referencia_auxiliar', rotulo: 'Referência auxiliar' },
  { valor: 'tipo', rotulo: 'Tipo' },
  { valor: 'bairro', rotulo: 'Bairro' },
  { valor: 'condominio', rotulo: 'Condomínio' },
  { valor: 'cidade', rotulo: 'Cidade' },
];

const SITUACOES = [
  { valor: '', rotulo: 'Todas as situações' },
  { valor: 'ativos', rotulo: 'Ativos' },
  { valor: 'inativos', rotulo: 'Inativos' },
  { valor: 'rascunhos', rotulo: 'Cadastro incompleto' },
  { valor: 'bloqueados', rotulo: 'Bloqueados' },
];

export default function ListaImoveis() {
  const toast = useToast();
  const { usuario } = useAuth();
  // `?q=` na URL (link "ver todos" da busca geral do Dashboard) já chega filtrado — ver mesmo
  // comentário em ListaClientes.js.
  const [searchParams] = useSearchParams();
  const [qInicial] = useState(() => searchParams.get('q') || '');
  const [empresas, setEmpresas] = useState([]);
  const [filtros, setFiltros] = useState(() => (qInicial ? { q: qInicial } : {}));
  const [form, setForm] = useState({ campo: '', q: qInicial, situacao: '', empresa_id: '' });
  const [imoveis, setImoveis] = useState(null);
  const [meta, setMeta] = useState(null);
  const [pagina, setPagina] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [erro, setErro] = useState(false);
  const [aberto, setAberto] = useState(null);
  const [avancado, setAvancado] = useState({}); // filtros da busca avançada já aplicados
  const [mostrarAvancada, setMostrarAvancada] = useState(false);
  const [tipos, setTipos] = useState([]);
  const [captadores, setCaptadores] = useState([]);
  const [recarregar, setRecarregar] = useState(0);

  useEffect(() => {
    if (usuario.nivel === 1) api.get('/empresas').then((r) => setEmpresas(r.data)).catch(() => {});
  }, [usuario.nivel]);

  // tipos e captadores pros seletores da busca avançada (superadmin: os da empresa escolhida)
  useEffect(() => {
    const q = usuario.nivel === 1 && form.empresa_id ? `?empresa_id=${form.empresa_id}` : '';
    api
      .get(`/imoveis/apoio${q}`)
      .then((r) => {
        setTipos(r.data.tipos);
        setCaptadores(r.data.captadores);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuario.nivel, form.empresa_id]);

  useEffect(() => {
    let cancelado = false;
    setErro(false);
    const query = new URLSearchParams({ page: pagina, per_page: perPage, ...filtros, ...avancado });
    api
      .get(`/imoveis?${query}`)
      .then((r) => {
        if (cancelado) return;
        setImoveis(r.data);
        setMeta(r.meta);
      })
      .catch((err) => {
        if (!cancelado) {
          setErro(true);
          toast.erro(err.message || 'Não foi possível carregar os imóveis');
        }
      });
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagina, perPage, filtros, avancado, recarregar]);

  function buscar(e) {
    e.preventDefault();
    const novos = {};
    for (const [chave, valor] of Object.entries(form)) if (valor) novos[chave] = valor;
    // o campo só faz sentido junto com um texto
    if (!novos.q) delete novos.campo;
    setFiltros(novos);
    setPagina(1);
  }

  function limpar() {
    setForm({ campo: '', q: '', situacao: '', empresa_id: '' });
    setFiltros({});
    setAvancado({});
    setPagina(1);
  }

  function aplicarAvancado(novos) {
    setAvancado(novos);
    setPagina(1);
  }

  function removerChip(chaves) {
    const restante = { ...avancado };
    chaves.forEach((k) => delete restante[k]);
    // tirar o tipo tira também as características dele
    if (chaves.includes('tipo')) delete restante.caracteristicas;
    setAvancado(restante);
    setPagina(1);
  }

  const chips = descreverFiltros(avancado, { tipos, captadores });

  return (
    <>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
        <h1 className="h4 mb-0">Imóveis</h1>
        {usuario.permissoes?.imoveis_criar === true && (
          <Button as={Link} to="/imoveis/novo" variant="primary">
            Adicionar imóvel
          </Button>
        )}
      </div>

      <Form onSubmit={buscar} className="mb-3">
        <Row className="g-2">
          {usuario.nivel === 1 && (
            <Col md={3}>
              <Form.Select aria-label="Empresa" value={form.empresa_id} onChange={(e) => setForm({ ...form, empresa_id: e.target.value })}>
                <option value="">Todas as empresas</option>
                {empresas.map((emp) => (
                  <option key={emp.public_id} value={emp.public_id}>
                    {emp.e1_nome}
                  </option>
                ))}
              </Form.Select>
            </Col>
          )}
          <Col md={usuario.nivel === 1 ? 5 : 7}>
            <InputGroup>
              <Form.Select aria-label="Buscar em" style={{ maxWidth: 190 }} value={form.campo} onChange={(e) => setForm({ ...form, campo: e.target.value })}>
                {CAMPOS_BUSCA.map((c) => (
                  <option key={c.valor} value={c.valor}>
                    {c.rotulo}
                  </option>
                ))}
              </Form.Select>
              <Form.Control type="search" placeholder="Buscar..." aria-label="Buscar" value={form.q} onChange={(e) => setForm({ ...form, q: e.target.value })} />
            </InputGroup>
          </Col>
          <Col md={2}>
            <Form.Select aria-label="Situação" value={form.situacao} onChange={(e) => setForm({ ...form, situacao: e.target.value })}>
              {SITUACOES.map((s) => (
                <option key={s.valor} value={s.valor}>
                  {s.rotulo}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md="auto" className="d-flex gap-2">
            <Button type="submit" variant="primary">
              Buscar
            </Button>
            <Button type="button" variant="outline-secondary" onClick={limpar}>
              Limpar
            </Button>
            <Button type="button" variant={chips.length ? 'secondary' : 'outline-secondary'} onClick={() => setMostrarAvancada((m) => !m)} aria-expanded={mostrarAvancada}>
              Busca avançada{chips.length ? ` (${chips.length})` : ''} {mostrarAvancada ? '▲' : '▼'}
            </Button>
          </Col>
        </Row>
      </Form>

      {mostrarAvancada && (
        <BuscaAvancadaImoveis
          valores={avancado}
          tipos={tipos}
          captadores={captadores}
          aoAplicar={(novos) => {
            aplicarAvancado(novos);
            setMostrarAvancada(false);
          }}
          aoLimpar={() => aplicarAvancado({})}
        />
      )}

      {chips.length > 0 && (
        <div className="d-flex flex-wrap gap-2 mb-3">
          {chips.map((chip) => (
            <Badge key={chip.texto} bg="primary" className="d-inline-flex align-items-center gap-2 py-2 px-3 fw-normal" style={{ fontSize: 13 }}>
              {chip.texto}
              <button type="button" className="btn-close btn-close-white" style={{ fontSize: 9 }} aria-label={`Remover filtro ${chip.texto}`} onClick={() => removerChip(chip.chaves)} />
            </Badge>
          ))}
        </div>
      )}

      {!imoveis && !erro && <Spinner animation="border" />}

      {imoveis && (
        <>
          <TabelaImoveis imoveis={imoveis} usuario={usuario} aoAbrir={setAberto} />
          {imoveis.length === 0 && (
            <p className="text-muted">
              {Object.keys(filtros).length + Object.keys(avancado).length > 0 ? 'Nenhum imóvel encontrado com esses filtros.' : 'Nenhum imóvel cadastrado ainda.'}
            </p>
          )}
          <Paginacao
            meta={meta}
            aoMudarPagina={setPagina}
            aoMudarPerPage={(n) => {
              setPerPage(n);
              setPagina(1);
            }}
          />
        </>
      )}

      <DetalheImovel
        publicId={aberto}
        aoFechar={() => setAberto(null)}
        aoExcluido={() => {
          setAberto(null);
          setRecarregar((n) => n + 1);
        }}
      />
    </>
  );
}
